/**
 * The per-type setup slot of the create-pool flow (ISSUE-1258, ISSUE-1334).
 *
 * Survivor keeps its own rules step (`CreatePoolRulesStep`, `draft.rules`).
 * Every other pool type fills the same step position through this registry:
 * its own draft, its own step title, its own "still missing" list and the
 * extra fields it adds to `/pool/create`. Each type's module lives in its own
 * feature folder, so the Squares, Strip Card and Pick'em builders never edit
 * the create flow itself.
 *
 * Pure, no React. `CreatePoolTypeSetupStep.tsx` is the component half.
 */
import { PICKEM_SETUP_STEP_TITLE } from "../pickem/pickemCopy";
import {
  buildPickemSetupPayload,
  EMPTY_PICKEM_SETUP_DRAFT,
  pickemSetupMissingItems,
  type typePickemSetupDraft,
  type typePickemSetupPayload,
} from "../pickem/pickemCreateSetupDraft";
import {
  buildSquaresSetupPayload,
  EMPTY_SQUARES_SETUP_DRAFT,
  squaresSetupGridCount,
  squaresSetupMissingItems,
  type typeSquaresSetupDraft,
  type typeSquaresSetupPayload,
} from "../squares/squaresCreateSetupDraft";
import {
  SQUARES_SETUP_STEP_TITLE,
  squaresCreateSubmitLabel,
} from "../squares/squaresCopy";
import { STRIP_CARD_SETUP_STEP_TITLE } from "../strips/stripCardCopy";
import {
  buildStripCardSetupPayload,
  EMPTY_STRIP_CARD_SETUP_DRAFT,
  stripCardSetupMissingItems,
  type typeStripCardSetupDraft,
  type typeStripCardSetupPayload,
} from "../strips/stripCardCreateSetupDraft";

export const SETUP_SLOT_POOL_TYPES = [
  "squares",
  "stripCard",
  "pickem",
] as const;
export type typeSetupSlotPoolType = (typeof SETUP_SLOT_POOL_TYPES)[number];

export type typePoolTypeSetups = {
  squares: typeSquaresSetupDraft;
  stripCard: typeStripCardSetupDraft;
  pickem: typePickemSetupDraft;
};

export const EMPTY_POOL_TYPE_SETUPS: typePoolTypeSetups = {
  squares: EMPTY_SQUARES_SETUP_DRAFT,
  stripCard: EMPTY_STRIP_CARD_SETUP_DRAFT,
  pickem: EMPTY_PICKEM_SETUP_DRAFT,
};

/** The fields a setup-slot type adds to the `/pool/create` body. */
export type typePoolTypeCreateFields = {
  squaresSetup?: typeSquaresSetupPayload;
  stripCardSetup?: typeStripCardSetupPayload;
  pickemSetup?: typePickemSetupPayload;
};

export function isSetupSlotPoolType(
  contestType: string | null,
): contestType is typeSetupSlotPoolType {
  return (
    contestType === "squares" ||
    contestType === "stripCard" ||
    contestType === "pickem"
  );
}

export const POOL_TYPE_SETUP_STEP_TITLES: Readonly<
  Record<typeSetupSlotPoolType, string>
> = {
  squares: SQUARES_SETUP_STEP_TITLE,
  stripCard: STRIP_CARD_SETUP_STEP_TITLE,
  pickem: PICKEM_SETUP_STEP_TITLE,
};

/**
 * MSG-3 / Q80. The chat switch starts where the pool type says: on for
 * every type except Strip Card. pool-api applies the same default when a
 * create sends none.
 */
export const POOL_TYPE_CHAT_DEFAULTS: Readonly<Record<string, boolean>> = {
  survivor: true,
  squares: true,
  stripCard: false,
  pickem: true,
};

export function poolTypeSetupMissingItems(
  contestType: typeSetupSlotPoolType,
  setups: typePoolTypeSetups,
): readonly string[] {
  if (contestType === "pickem") return pickemSetupMissingItems(setups.pickem);
  return contestType === "squares"
    ? squaresSetupMissingItems(setups.squares)
    : stripCardSetupMissingItems(setups.stripCard);
}

export function poolTypeCreateFields(
  contestType: typeSetupSlotPoolType,
  setups: typePoolTypeSetups,
): typePoolTypeCreateFields | null {
  if (contestType === "squares") {
    const squaresSetup = buildSquaresSetupPayload(setups.squares);
    return squaresSetup === null ? null : { squaresSetup };
  }
  if (contestType === "pickem") {
    const pickemSetup = buildPickemSetupPayload(setups.pickem);
    return pickemSetup === null ? null : { pickemSetup };
  }
  const stripCardSetup = buildStripCardSetupPayload(setups.stripCard);
  return stripCardSetup === null ? null : { stripCardSetup };
}

export const CREATE_POOL_SUBMIT_LABEL = "Create pool";

/**
 * The create footer's last button. A Squares pool names the grids it makes
 * ("Create 14 grids", "Create grid"); every other pool type reads "Create
 * pool" (ISSUE-1269, owner pick 2026-11-03).
 */
export function createPoolSubmitLabel(
  contestType: string | null,
  setups: typePoolTypeSetups,
): string {
  if (contestType !== "squares") return CREATE_POOL_SUBMIT_LABEL;
  return squaresCreateSubmitLabel(squaresSetupGridCount(setups.squares));
}
